import React, { useContext, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { Button, CircularProgress } from "@mui/material";
import { query, getDocs } from "firebase/firestore";
import PropertyContext from "../contexts/PropertyContext";
import { propertyCollection } from "../utils/firestorePaths";
import { downloadBlob } from "../utils/reports/downloadBlob";
import { buildReportFilename } from "../utils/reports/reportFilename";

// Helper function to get Excel column letter given a column number
const getExcelColumnLetter = (colNum) => {
  let temp,
    letter = "";
  while (colNum > 0) {
    temp = (colNum - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    colNum = (colNum - temp - 1) / 26;
  }
  return letter;
};

const GenerateDRList = () => {
  const [, setItemsByCategory] = useState({});
  const [loading, setLoading] = useState(false);
  const { selectedProperty, selectedClient, rooms, items } = useContext(PropertyContext);

  // Helper functions for conversion using mapping objects
  const convertHousekeeping = (req) => {
    const housekeepingMapping = {
      Refresh: 15,
      Easy: 45,
      Medium: 90,
      Heavy: 120,
      "Very Heavy": 180,
      Completed: 0,
    };
    return housekeepingMapping[req] ?? 0;
  };

  const convertMaintenance = (req) => {
    const maintenanceMapping = {
      Easy: 30,
      Medium: 60,
      Heavy: 90,
      "Very Heavy": 120,
      Completed: 0,
    };
    return maintenanceMapping[req] ?? 0;
  };

  const convertColorNameToARGB = (colorName) => {
    const colorMapping = {
      blue: "FF0000FF",
      yellow: "FFFFFF00",
      red: "FFFF0000",
    };
    return colorMapping[colorName] || "FFFFFFFF";
  };

  // Helper function to format minutes into "H:MM" format
  const formatMinutesToHours = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
  };

  const generateExcelDRL = async () => {
    setLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const mainWorksheet = workbook.addWorksheet("DRL Overview");

      // Create columns dynamically based on item names
      const itemColumns = items.map((item) => ({
        header: item.itemName,
        key: item.itemName,
        width: 20,
      }));

      const vendors = [
        { key: "vendorElectricalRepair", expectedCost: 500, color: "yellow" },
        { key: "vendorPestControl", expectedCost: 350, color: "blue" },
        { key: "vendorPlumbingRepair", expectedCost: 200, color: "red" },
        { key: "vendorSubfloor", expectedCost: 1600, color: "red" },
        { key: "vendorTubResurface", expectedCost: 600, color: "red" },
        { key: "vendorWindow", expectedCost: 500, color: "red" },
      ];

      // Define main worksheet columns including the requirements columns
      mainWorksheet.columns = [
        { header: "Room", key: "room", width: 20 },
        {
          header: "Housekeeping",
          key: "selectedHousekeepingRequirements",
          width: 30,
        },
        {
          header: "Maintenance",
          key: "selectedMaintenanceRequirements",
          width: 30,
        },
        ...itemColumns,
        ...vendors.map((vendor) => ({
          header: vendor.key.replace("vendor", "").trim(),
          key: vendor.key,
          width: 20,
        })),
        { header: "Total", key: "total", width: 20 },
        { header: "Unit Notes", key: "unitNotes", width: 50 },
      ];

      mainWorksheet.getRow(1).font = { bold: true };
      mainWorksheet.getRow(1).alignment = { horizontal: "center" };
      mainWorksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD3D3D3" },
      };

      // Filter rooms to only include those with status 'Down' or 'Facilities'
      const downRooms = rooms.filter(
        (room) => room.status === "Down" || room.status === "Facilities"
      );

      // Collect row data in an array so we can sort before adding to the worksheet
      let rowDatas = [];

      for (const room of downRooms) {
        const roomWorksheet = workbook.addWorksheet(room.displayName);

        // Start with base requirements values (in minutes)
        let housekeepingTotal = convertHousekeeping(
          room.selectedHousekeepingRequirements
        );
        let maintenanceTotal = convertMaintenance(
          room.selectedMaintenanceRequirements
        );

        let rowData = {
          room: {
            text: room.displayName,
            hyperlink: `#'${room.displayName}'!A1`,
          },
          selectedHousekeepingRequirements: housekeepingTotal,
          selectedMaintenanceRequirements: maintenanceTotal,
        };

        let totalCost = 0;

        // Process item columns and add difficulty adjustments
        items.forEach((item) => {
          const itemCountKey = item.itemID;
          const itemCount = parseFloat(room[itemCountKey] || 0);
          const itemCost = parseFloat(item.itemCost || 0);
          const itemDifficulty = parseFloat(item.itemDifficulty || 0);
          rowData[item.itemName] = itemCount > 0 ? itemCount : "";
          totalCost += itemCount * itemCost;
          if (itemCount > 0) {
            if (item.itemResponsibility === "Maintenance") {
              maintenanceTotal += itemCount * itemDifficulty;
            } else if (item.itemResponsibility === "Housekeeping") {
              housekeepingTotal += itemCount * itemDifficulty;
            }
          }
        });

        // Format the requirements values as hours:minutes
        rowData.selectedHousekeepingRequirements =
          formatMinutesToHours(housekeepingTotal);
        rowData.selectedMaintenanceRequirements =
          formatMinutesToHours(maintenanceTotal);

        // Process vendor columns
        vendors.forEach((vendor) => {
          const vendorCount = parseInt(room[vendor.key] || 0, 10);
          rowData[vendor.key] = vendorCount > 0 ? vendorCount : "";
          totalCost += vendorCount * vendor.expectedCost;
        });

        totalCost += 15 * (housekeepingTotal / 60);
        totalCost += 25 * (maintenanceTotal / 60);

        rowData.total = totalCost; // Keep as number for currency formatting
        rowData.unitNotes = room.unitNotes || "";

        // Determine the vendor color for the room:
        // Priority: Pest Control (blue), then Electrical (yellow), then any other vendor (red).
        let vendorColor = "";
        if (parseInt(room["vendorPestControl"] || 0, 10) > 0) {
          vendorColor = "blue";
        } else if (parseInt(room["vendorElectricalRepair"] || 0, 10) > 0) {
          vendorColor = "yellow";
        } else {
          const otherVendors = [
            "vendorPlumbingRepair",
            "vendorSubfloor",
            "vendorTubResurface",
            "vendorWindow",
          ];
          for (let key of otherVendors) {
            if (parseInt(room[key] || 0, 10) > 0) {
              vendorColor = "red";
              break;
            }
          }
        }
        rowData.vendorColor = vendorColor;

        rowDatas.push(rowData);

        // Add columns to the room worksheet
        roomWorksheet.columns = [
          { header: "Room", key: "room", width: 20 },
          ...itemColumns,
        ];
        roomWorksheet.getRow(1).font = { bold: true };
        roomWorksheet.getRow(1).alignment = { horizontal: "center" };
        roomWorksheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD3D3D3" },
        };

        // Add room data to the room worksheet
        const roomRowData = { room: room.displayName };
        items.forEach((item) => {
          roomRowData[item.itemName] = room[item.itemID] || "";
        });
        roomWorksheet.addRow(roomRowData);

        // Add image links to the room worksheet
        const imagesRef = propertyCollection(
          selectedClient,
          selectedProperty,
          "units",
          room.id,
          "images"
        );

        const imagesQuerySnapshot = await getDocs(query(imagesRef));
        let imageRow = 3;
        for (const doc of imagesQuerySnapshot.docs) {
          const image = doc.data();
          roomWorksheet.addRow([
            `Uploaded at: ${image.uploadedAt}`,
            `Link: ${image.url}`,
          ]);
          const cell = roomWorksheet.getCell(`B${imageRow}`);
          cell.value = {
            text: "View Image",
            hyperlink: image.url,
          };
          cell.font = { color: { argb: "FF0000FF" }, underline: true };
          imageRow += 1;
        }
      }

      // Sort the row data by total cost (lowest to highest)
      rowDatas.sort((a, b) => a.total - b.total);

      // Add sorted rows to the main worksheet and apply cell fill for the "Room" column
      rowDatas.forEach((data) => {
        const newRow = mainWorksheet.addRow(data);
        if (data.vendorColor) {
          newRow.getCell("room").fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: convertColorNameToARGB(data.vendorColor) },
          };
        }
      });

      // Format the "Total" column as USD currency
      mainWorksheet.getColumn("total").numFmt = "$#,##0.00";

      // Determine the table range based on the number of rows and columns
      const totalRows = mainWorksheet.rowCount; // includes header
      const totalCols = mainWorksheet.columns.length;
      const lastColLetter = getExcelColumnLetter(totalCols);
      // eslint-disable-next-line no-unused-vars
      const tableRange = `A1:${lastColLetter}${totalRows}`;

      // Create an Excel table to wrap the data
      mainWorksheet.addTable({
        name: "DRLTable",
        ref: "A1",
        headerRow: true,
        totalsRow: false,
        style: {
          theme: "TableStyleMedium9",
          showRowStripes: true,
        },
        columns: mainWorksheet.columns.map((col) => ({ name: col.header })),
        rows: rowDatas.map((data) =>
          mainWorksheet.columns.map((col) => {
            const value = data[col.key];
            return value && typeof value === "object" && value.text
              ? value.text
              : value;
          })
        ),
      });

      // Apply borders to all cells
      mainWorksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(blob, buildReportFilename("Generated_DRL", { ext: "xlsx" }));
    } catch (error) {
      console.error("Error generating DRL:", error);
      alert("Error generating DRL. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const groupItemsByType = (items) => {
    return items.reduce((acc, item) => {
      if (!acc[item.itemType]) {
        acc[item.itemType] = [];
      }
      acc[item.itemType].push(item);
      return acc;
    }, {});
  };

  useEffect(() => {
    const groupedItems = groupItemsByType(items);

    const newItemType = "Vendor Needed";
    if (!groupedItems[newItemType]) {
      groupedItems[newItemType] = [];
    }

    const vendorsList = [
      { itemID: "vendorElectricalRepair", itemName: "Electrical Repair" },
      { itemID: "vendorPestControl", itemName: "Pest Control" },
      { itemID: "vendorPlumbingRepair", itemName: "Plumbing Repair" },
      { itemID: "vendorTubResurface", itemName: "Tub Resurface" },
      { itemID: "vendorWindow", itemName: "Window" },
    ];

    vendorsList.forEach((item) => {
      groupedItems[newItemType].push({
        ...item,
        itemType: newItemType,
      });
    });

    setItemsByCategory(groupedItems);
  }, [items]);

  return (
    <Button
      variant="contained"
      fullWidth
      onClick={generateExcelDRL}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={16} /> : null}
    >
      {loading ? "Generating..." : "DRL Excel"}
    </Button>
  );
};

export default GenerateDRList;
